import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "@/lib/api";
import type {
  SuperadminMetricsResponse,
  SuperadminTenantsResponse,
  SuperadminUsersResponse,
  PlatformAuditResponse,
} from "@/types/api";

// ── Platform Metrics ──────────────────────────────────────

export function useSuperadminMetrics() {
  return useQuery<SuperadminMetricsResponse>({
    queryKey: ["superadmin", "metrics"],
    queryFn: () => apiGet<SuperadminMetricsResponse>("/api/superadmin/metrics"),
    refetchInterval: 60_000,
  });
}

// ── Tenants ───────────────────────────────────────────────

export function useSuperadminTenants(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  plan?: string;
}) {
  return useQuery<SuperadminTenantsResponse>({
    queryKey: ["superadmin", "tenants", params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      if (params?.search) qs.search = params.search;
      if (params?.status) qs.status = params.status;
      if (params?.plan) qs.plan = params.plan;
      return apiGet<SuperadminTenantsResponse>("/api/superadmin/tenants", qs);
    },
  });
}

export function useUpdateTenantPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, plan }: { tenantId: string; plan: string }) =>
      apiPut(`/api/superadmin/tenants/${tenantId}/plan`, { plan }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] });
      qc.invalidateQueries({ queryKey: ["superadmin", "metrics"] });
    },
  });
}

export function useUpdateTenantTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, tier }: { tenantId: string; tier: string }) =>
      apiPut(`/api/superadmin/tenants/${tenantId}/tier`, { tier }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] });
    },
  });
}

export function useUpdateTenantStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, status }: { tenantId: string; status: string }) =>
      apiPut(`/api/superadmin/tenants/${tenantId}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] });
      qc.invalidateQueries({ queryKey: ["superadmin", "metrics"] });
    },
  });
}

// ── Users ─────────────────────────────────────────────────

export function useSuperadminUsers(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  tenant_id?: string;
  role?: string;
}) {
  return useQuery<SuperadminUsersResponse>({
    queryKey: ["superadmin", "users", params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      if (params?.search) qs.search = params.search;
      if (params?.tenant_id) qs.tenant_id = params.tenant_id;
      if (params?.role) qs.role = params.role;
      return apiGet<SuperadminUsersResponse>("/api/superadmin/users", qs);
    },
  });
}

// ── Impersonation ────────────────────────────────────────

export function useLogImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      apiPost(`/api/superadmin/impersonate/${tenantId}`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "audit-log"] });
    },
  });
}

// ── Audit Log ─────────────────────────────────────────────

export function useSuperadminAuditLog(params?: {
  limit?: number;
  offset?: number;
}) {
  return useQuery<PlatformAuditResponse>({
    queryKey: ["superadmin", "audit-log", params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      return apiGet<PlatformAuditResponse>("/api/superadmin/audit-log", qs);
    },
  });
}
